import noop from 'lodash/noop';
import { z } from 'zod';

import { createSkillDetailTilePayload } from '~/feature/skills';

import {
    createUrlTileDefinition,
    type TilePermissionResolver,
    type UrlTileDefinition,
    type UrlTileModule,
    type UrlTileRenderContext,
} from '../url-tiles';
import { useLearningOpportunitiesPermissions } from './api';
import type { SidepanelMode } from './form-state';
import { LearningOpportunitySidepanel } from './LearningOpportunitySidepanel';

export const LEARNING_OPPORTUNITY_TILE_SLOT_KEY = 'learning-opportunity.detail';
export const LEARNING_OPPORTUNITY_CREATE_TILE_TYPE = 'v1.learning-opportunity.create';
export const LEARNING_OPPORTUNITY_DETAIL_TILE_TYPE = 'v1.learning-opportunity.detail';
export const LEARNING_OPPORTUNITY_EDIT_TILE_TYPE = 'v1.learning-opportunity.edit';

const learningOpportunityCreateTileSchema = z.object({
    type: z.literal(LEARNING_OPPORTUNITY_CREATE_TILE_TYPE),
});

const learningOpportunityDetailTileSchema = z.object({
    opportunityId: z.uuid(),
    type: z.literal(LEARNING_OPPORTUNITY_DETAIL_TILE_TYPE),
});

const learningOpportunityEditTileSchema = z.object({
    opportunityId: z.uuid(),
    type: z.literal(LEARNING_OPPORTUNITY_EDIT_TILE_TYPE),
});

export type LearningOpportunityCreateTilePayload = z.infer<typeof learningOpportunityCreateTileSchema>;
export type LearningOpportunityDetailTilePayload = z.infer<typeof learningOpportunityDetailTileSchema>;
export type LearningOpportunityEditTilePayload = z.infer<typeof learningOpportunityEditTileSchema>;
export type LearningOpportunityTilePayload =
    | LearningOpportunityCreateTilePayload
    | LearningOpportunityDetailTilePayload
    | LearningOpportunityEditTilePayload;

export const createLearningOpportunityCreateTilePayload = (): LearningOpportunityCreateTilePayload => ({
    type: LEARNING_OPPORTUNITY_CREATE_TILE_TYPE,
});

export const createLearningOpportunityDetailTilePayload = (
    opportunityId: string,
): LearningOpportunityDetailTilePayload => ({
    opportunityId,
    type: LEARNING_OPPORTUNITY_DETAIL_TILE_TYPE,
});

export const createLearningOpportunityEditTilePayload = (
    opportunityId: string,
): LearningOpportunityEditTilePayload => ({
    opportunityId,
    type: LEARNING_OPPORTUNITY_EDIT_TILE_TYPE,
});

const tileTypeToSidepanelMode: Record<LearningOpportunityTilePayload['type'], SidepanelMode> = {
    [LEARNING_OPPORTUNITY_CREATE_TILE_TYPE]: 'create',
    [LEARNING_OPPORTUNITY_DETAIL_TILE_TYPE]: 'view',
    [LEARNING_OPPORTUNITY_EDIT_TILE_TYPE]: 'edit',
};

const createLearningOpportunityTileProps = (payload: LearningOpportunityTilePayload, context: UrlTileRenderContext) =>
    ({
        mode: tileTypeToSidepanelMode[payload.type],
        onClose: () => context.closeTile(LEARNING_OPPORTUNITY_TILE_SLOT_KEY),
        onSkillClick: (skillId: string, preferredLanguage?: string) => {
            context.openTile(createSkillDetailTilePayload({ preferredLanguage, skillId }));
        },
        // Trained-skills tracking is intentionally a no-op in the tile context.
        onTrainedSkillsChange: noop,
        opportunityId: 'opportunityId' in payload ? payload.opportunityId : undefined,
    }) as const;

type LearningOpportunityTileProps = ReturnType<typeof createLearningOpportunityTileProps>;

const renderLearningOpportunityTile = (props: LearningOpportunityTileProps) => (
    <LearningOpportunitySidepanel
        mode={props.mode}
        opportunityId={props.opportunityId}
        onClose={props.onClose}
        onSkillClick={props.onSkillClick}
        onTrainedSkillsChange={props.onTrainedSkillsChange}
    />
);

export const learningOpportunityCreateUrlTileDefinition = createUrlTileDefinition<
    LearningOpportunityCreateTilePayload,
    LearningOpportunityTileProps
>({
    maxWidth: '50%',
    minWidth: '50%',
    parseTokens: (tokens) => ({
        type: tokens.slice(0, 3).join('.'),
    }),
    render: renderLearningOpportunityTile,
    schema: learningOpportunityCreateTileSchema,
    serialize: (payload) => payload.type,
    slotKey: () => LEARNING_OPPORTUNITY_TILE_SLOT_KEY,
    tileKey: (payload) => payload.type,
    toProps: createLearningOpportunityTileProps,
    type: LEARNING_OPPORTUNITY_CREATE_TILE_TYPE,
});

export const learningOpportunityDetailUrlTileDefinition = createUrlTileDefinition<
    LearningOpportunityDetailTilePayload,
    LearningOpportunityTileProps
>({
    maxWidth: '50%',
    minWidth: '50%',
    parseTokens: (tokens) => ({
        opportunityId: tokens[3],
        type: tokens.slice(0, 3).join('.'),
    }),
    render: renderLearningOpportunityTile,
    schema: learningOpportunityDetailTileSchema,
    serialize: (payload) => `${payload.type}.${payload.opportunityId}`,
    slotKey: () => LEARNING_OPPORTUNITY_TILE_SLOT_KEY,
    tileKey: (payload) => `${LEARNING_OPPORTUNITY_TILE_SLOT_KEY}.${payload.opportunityId}.view`,
    toProps: createLearningOpportunityTileProps,
    type: LEARNING_OPPORTUNITY_DETAIL_TILE_TYPE,
});

export const learningOpportunityEditUrlTileDefinition = createUrlTileDefinition<
    LearningOpportunityEditTilePayload,
    LearningOpportunityTileProps
>({
    maxWidth: '50%',
    minWidth: '50%',
    parseTokens: (tokens) => ({
        opportunityId: tokens[3],
        type: tokens.slice(0, 3).join('.'),
    }),
    render: renderLearningOpportunityTile,
    schema: learningOpportunityEditTileSchema,
    serialize: (payload) => `${payload.type}.${payload.opportunityId}`,
    slotKey: () => LEARNING_OPPORTUNITY_TILE_SLOT_KEY,
    tileKey: (payload) => `${LEARNING_OPPORTUNITY_TILE_SLOT_KEY}.${payload.opportunityId}.edit`,
    toProps: createLearningOpportunityTileProps,
    type: LEARNING_OPPORTUNITY_EDIT_TILE_TYPE,
});

export const learningOpportunityUrlTileDefinitions: readonly UrlTileDefinition[] = [
    learningOpportunityCreateUrlTileDefinition,
    learningOpportunityDetailUrlTileDefinition,
    learningOpportunityEditUrlTileDefinition,
];

const useCreateLearningOpportunitiesTilePermission: TilePermissionResolver = () => {
    const { canUserCreateLearningOpportunities, isPending } = useLearningOpportunitiesPermissions();

    return { isAllowed: canUserCreateLearningOpportunities, isLoading: isPending };
};

const useManageLearningOpportunitiesTilePermission: TilePermissionResolver = () => {
    const { canUserSeeLearningOpportunities, isPending } = useLearningOpportunitiesPermissions();

    return { isAllowed: canUserSeeLearningOpportunities, isLoading: isPending };
};

export const learningOpportunityUrlTileModule: UrlTileModule = {
    definitions: learningOpportunityUrlTileDefinitions,
    permissionResolvers: {
        createLearningOpportunities: useCreateLearningOpportunitiesTilePermission,
        manageLearningOpportunities: useManageLearningOpportunitiesTilePermission,
    },
};
